import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SteeringWheelIcon } from "../../app/common/steering_wheel_icon";
import {
  hasAuthorizedWheel,
  isWheelHidSupported,
  requestWheelPermission,
  useWheelMenuNav,
  type WheelMenuEvent,
} from "../../hooks/useWheelMenuNav";
import type {
  ExternalDisplayRole,
  ExternalDisplayRoleStatus,
} from "../../app/display/external_display_types";
import { WheelHelpOverlay } from "./wheel_help_overlay";
import "./device_setup.css";

// 로그인(인증) 직후 ~ 차량(플릿) 선택 직전에 표시되는 "장치 준비" 단계.
// 콕핏 운영에 필요한 각 장치/권한(전체화면 · 좌/우 보조 화면 · 마이크 · 게이밍 휠)을
// 점검하고, 미준비 항목은 "요청" 동작으로 즉석에서 확보한 뒤 다음(차량 선택)으로 진행한다.
//
// 단계별 사용자 제스처(브라우저 제약):
//   각 브라우저 동작(팝업 열기 / 권한 부여 / 전체화면)은 독립된 사용자 제스처를 요구하므로,
//   게이밍 휠 가속페달(CONFIRM=pedalConfirm)을 한 번 누를 때마다 "다음 미준비 단계"의
//   요청 동작 하나만 실행하고 포커스를 진행시킨다. 키보드/마우스도 동일하게 동작.
//
// 모두 준비되어 있으면(권한 보유 재방문 등) 자동으로 다음 단계로 넘어가 사용자를 막지 않는다.

interface DeviceSetupProps {
  loginId: string;
  // 모두 준비되어 다음(차량 선택) 단계로 진행할 때 호출.
  onComplete: () => void;
  // 로그아웃(휠 BACK / 하단 링크).
  onLogout: () => void;
  // 보조 모니터(외부 디스플레이) 지원 여부 — 단일 모니터/미지원이면 해당 단계 숨김(N/A).
  displaysSupported: boolean;
  // 보조 모니터가 모두 펼쳐졌는지(또는 펼칠 게 없어 준비완료인지) — 자동 진행 게이트 보강용.
  displaysReady: boolean;
  // role(좌/우)별 열림 + 전체화면 세부 상태(행 분리 표시 + 게이트 판단).
  displayStatuses: ExternalDisplayRoleStatus[];
  // 보조 모니터 (재)펼치기 — 사용자 제스처 컨텍스트에서 호출.
  onOpenDisplays: () => void;
}

type Readiness = "ready" | "notready" | "na";

interface StepView {
  key: string;
  label: string;
  hint: string;
  icon: React.ReactNode;
  readiness: Readiness;
  // 미준비일 때 표시/실행할 요청 동작. na 이거나 요청 불가면 undefined.
  onRequest?: () => void;
  requestLabel?: string;
  // 이 단계가 진행(다음) 게이트에 포함되는 필수 항목인지.
  required: boolean;
}

const POLL_INTERVAL_MS = 1200;

function isFullscreenActive(): boolean {
  if (typeof document === "undefined") return false;
  // ① 표준 Fullscreen API (F11/requestFullscreen) 로 진입한 "진짜" 전체화면.
  if (document.fullscreenElement) return true;
  // ② PWA 를 display:"fullscreen" 으로 설치/실행한 경우만 전체화면으로 인정.
  //    (standalone/minimal-ui 는 주소창/타이틀바가 남는 비-전체화면이라 제외 — 이걸 인정하면
  //     실제로 전체화면이 아닌데 준비 단계가 넘어가는 문제가 생긴다.)
  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    try {
      return window.matchMedia("(display-mode: fullscreen)").matches;
    } catch {
      // matchMedia 미지원 — 표준 API 결과(false)로 폴백.
    }
  }
  return false;
}

function requestAppFullscreen(): void {
  if (typeof document === "undefined") return;
  const el = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
  };
  if (document.fullscreenElement) return;
  try {
    const req = el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.();
    if (req && typeof (req as Promise<void>).catch === "function") {
      (req as Promise<void>).catch(() => {});
    }
  } catch {
    // no-op
  }
}

function roleLabel(role: ExternalDisplayRole): string {
  return role === "left" ? "좌측 화면" : "우측 화면";
}

// ── 인라인 SVG 아이콘 (이모지 금지, currentColor 상속) ──
const FullscreenIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 9V5a1 1 0 0 1 1-1h4" />
    <path d="M20 9V5a1 1 0 0 0-1-1h-4" />
    <path d="M4 15v4a1 1 0 0 0 1 1h4" />
    <path d="M20 15v4a1 1 0 0 1-1 1h-4" />
  </svg>
);

const MonitorIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2.5" y="4" width="19" height="12" rx="1.5" />
    <line x1="8.5" y1="20" x2="15.5" y2="20" />
    <line x1="12" y1="16" x2="12" y2="20" />
  </svg>
);

const MicIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <line x1="12" y1="18" x2="12" y2="21" />
    <line x1="8.5" y1="21" x2="15.5" y2="21" />
  </svg>
);

// 상태 표시 점(체크/느낌표) — 색맹 접근성 위해 색 + 기호 병용.
const CheckGlyph = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 8.5l3.2 3.2L13 5" />
  </svg>
);
const WarnGlyph = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
    <line x1="8" y1="3.5" x2="8" y2="9.5" />
    <circle cx="8" cy="12.4" r="0.6" fill="currentColor" stroke="none" />
  </svg>
);

function StatusPill({ readiness }: { readiness: Readiness }) {
  if (readiness === "ready") {
    return (
      <span className="ds-status ready">
        <span className="ds-status-dot"><CheckGlyph /></span>
        준비됨
      </span>
    );
  }
  if (readiness === "na") {
    return <span className="ds-status na">해당 없음</span>;
  }
  return (
    <span className="ds-status notready">
      <span className="ds-status-dot"><WarnGlyph /></span>
      미준비
    </span>
  );
}

// 좌/우 화면 행에서 "열림 ✓ / 전체화면 ✗" 두 하위 상태를 함께 표기.
function SubStatus({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span className={`ds-substatus${ok ? " ok" : ""}`}>
      <span className="ds-substatus-glyph">
        {ok ? <CheckGlyph /> : <WarnGlyph />}
      </span>
      {label}
    </span>
  );
}

const DeviceSetupComponent = (props: DeviceSetupProps) => {
  const {
    loginId,
    onComplete,
    onLogout,
    displaysSupported,
    displaysReady,
    displayStatuses,
    onOpenDisplays,
  } = props;

  const hidSupported = isWheelHidSupported();

  // ── 개별 항목 준비 상태(폴링으로 갱신) ──
  const [fullscreenReady, setFullscreenReady] = useState(isFullscreenActive);
  const [micReady, setMicReady] = useState(false);
  const [wheelReady, setWheelReady] = useState(false);
  const [wheelConnected, setWheelConnected] = useState(false);
  const [requesting, setRequesting] = useState<string | null>(null);

  // 자동 진행을 1회만 시도하기 위한 가드.
  const autoAdvancedRef = useRef(false);

  // 마이크 권한 상태 조회(가능하면 Permissions API, 아니면 getUserMedia 로 확인).
  const refreshMicReady = useCallback(async () => {
    try {
      const perms = (navigator as Navigator & {
        permissions?: Permissions;
      }).permissions;
      if (perms?.query) {
        const status = await perms.query({
          name: "microphone" as PermissionName,
        });
        setMicReady(status.state === "granted");
        return;
      }
    } catch {
      // Permissions API 미지원/거부 — 아래 폴백으로 진행.
    }
    // 폴백: enumerateDevices 에서 라벨이 보이면 권한이 부여된 상태.
    try {
      const devices = await navigator.mediaDevices?.enumerateDevices();
      const hasLabeledMic = (devices ?? []).some(
        (d) => d.kind === "audioinput" && d.label,
      );
      setMicReady(hasLabeledMic);
    } catch {
      setMicReady(false);
    }
  }, []);

  const refreshWheelReady = useCallback(async () => {
    if (!hidSupported) {
      setWheelReady(false);
      return;
    }
    const ok = await hasAuthorizedWheel();
    setWheelReady(ok);
  }, [hidSupported]);

  // 마운트 + 주기적으로 모든 항목 재점검(요청 후 자동 반영 위함).
  useEffect(() => {
    let cancelled = false;
    const refreshAll = () => {
      if (cancelled) return;
      setFullscreenReady(isFullscreenActive());
      void refreshMicReady();
      void refreshWheelReady();
    };
    refreshAll();
    const id = window.setInterval(refreshAll, POLL_INTERVAL_MS);
    document.addEventListener("fullscreenchange", refreshAll);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("fullscreenchange", refreshAll);
    };
  }, [refreshMicReady, refreshWheelReady]);

  // ── 요청 핸들러 ──
  const onRequestFullscreen = useCallback(() => {
    requestAppFullscreen();
    // fullscreenchange 이벤트가 상태를 갱신하지만, 즉시성 위해 한 틱 뒤 재확인.
    window.setTimeout(() => setFullscreenReady(isFullscreenActive()), 120);
  }, []);

  const onRequestMic = useCallback(async () => {
    setRequesting("mic");
    try {
      const stream = await navigator.mediaDevices?.getUserMedia({ audio: true });
      stream?.getTracks().forEach((t) => t.stop());
    } catch {
      // 거부/미지원 — 무시. 폴링이 상태를 유지.
    } finally {
      await refreshMicReady();
      setRequesting(null);
    }
  }, [refreshMicReady]);

  const onRequestWheel = useCallback(async () => {
    setRequesting("wheel");
    try {
      const ok = await requestWheelPermission();
      setWheelReady(ok);
    } finally {
      await refreshWheelReady();
      setRequesting(null);
    }
  }, [refreshWheelReady]);

  // 좌/우 화면을 (재)펼치기 — 아직 안 열린 창을 연다(사용자 제스처 컨텍스트).
  const onRequestOpenDisplays = useCallback(() => {
    requestAppFullscreen();
    onOpenDisplays();
  }, [onOpenDisplays]);

  // role 별 상태 조회 헬퍼.
  const statusByRole = useMemo(() => {
    const map: Partial<Record<ExternalDisplayRole, ExternalDisplayRoleStatus>> = {};
    displayStatuses.forEach((s) => {
      map[s.role] = s;
    });
    return map;
  }, [displayStatuses]);

  // ── 단계 뷰 모델 구성 ──
  const steps: StepView[] = useMemo(() => {
    const list: StepView[] = [];

    list.push({
      key: "fullscreen",
      label: "전체화면",
      hint: "콕핏 몰입 모드(메인 화면)로 전환합니다.",
      icon: <FullscreenIcon />,
      readiness: fullscreenReady ? "ready" : "notready",
      onRequest: fullscreenReady ? undefined : onRequestFullscreen,
      requestLabel: "전체화면",
      required: true,
    });

    // 좌/우 화면을 별도 행으로. 준비 기준 = "열림(open)" + "몰입(fullscreen)".
    // 여기서 fullscreen 은 팝업이 보고한 immersive 상태(진짜 전체화면 OR "주소창만 있는
    // 전체화면" = 모니터를 가득 채운 maximized)다. 페달로 창을 열면 화면 좌표로 위치/크기
    // 보정되어 maximized 가 되므로 이 상태까지 도달해야 준비됨. 가득 안 찼으면 다시 펼치기.
    if (displaysSupported) {
      displayStatuses.forEach((status) => {
        const ready = status.open && status.fullscreen;
        const needsOpen = !status.open;
        list.push({
          key: `display-${status.role}`,
          label: roleLabel(status.role),
          hint: needsOpen
            ? "가속페달로 보조 모니터 창을 엽니다."
            : ready
              ? "전체화면 준비됨."
              : "창을 모니터에 가득 채웁니다(가속페달로 재배치).",
          icon: <MonitorIcon />,
          readiness: ready ? "ready" : "notready",
          onRequest: ready ? undefined : onRequestOpenDisplays,
          requestLabel: needsOpen ? "창 열기" : "전체화면 맞춤",
          required: true,
        });
      });
    }

    list.push({
      key: "mic",
      label: "마이크 (음성)",
      hint: "콕핏 ↔ 차량 음성 통화에 사용합니다.",
      icon: <MicIcon />,
      readiness: micReady ? "ready" : "notready",
      onRequest: micReady ? undefined : () => void onRequestMic(),
      requestLabel: requesting === "mic" ? "요청 중…" : "권한 요청",
      required: true,
    });

    // 게이밍 휠 — WebHID 미지원이면 N/A(선택), 지원이면 권장(선택).
    list.push({
      key: "wheel",
      label: "게이밍 휠",
      hint: hidSupported
        ? "직접 조향·페달 조작 및 메뉴 내비게이션에 사용(선택)."
        : "이 브라우저는 게이밍 휠(WebHID)을 지원하지 않습니다.",
      icon: <SteeringWheelIcon className="ds-wheel-svg" />,
      readiness: !hidSupported ? "na" : wheelReady ? "ready" : "notready",
      onRequest:
        !hidSupported || wheelReady ? undefined : () => void onRequestWheel(),
      requestLabel: requesting === "wheel" ? "요청 중…" : "연결 권한",
      required: false,
    });

    return list;
  }, [
    fullscreenReady,
    displaysSupported,
    displayStatuses,
    micReady,
    hidSupported,
    wheelReady,
    requesting,
    onRequestFullscreen,
    onRequestOpenDisplays,
    onRequestMic,
    onRequestWheel,
  ]);

  // 모든 "필수" 단계가 준비됐는지. (보조 화면은 displaysReady 게이트로도 보강 — 행이
  // 비어 있는 순간을 대비해 displaysSupported 일 때 displaysReady 를 추가 조건으로.)
  const allRequiredReady = useMemo(() => {
    const stepsOk = steps.every((s) => !s.required || s.readiness === "ready");
    const displaysOk = !displaysSupported || displaysReady;
    return stepsOk && displaysOk;
  }, [steps, displaysSupported, displaysReady]);

  // ── 자동 진행: 진입 시(또는 폴링 첫 결과) 이미 모두 준비됐으면 곧장 다음으로 ──
  useEffect(() => {
    if (autoAdvancedRef.current) return;
    if (allRequiredReady) {
      autoAdvancedRef.current = true;
      onComplete();
    }
  }, [allRequiredReady, onComplete]);

  // ── 키보드/휠 포커스 대상: 미준비(요청 동작 있는) 단계들 + 진행 버튼 ──
  const focusTargets = useMemo(() => {
    const arr: Array<{ kind: "step"; index: number } | { kind: "proceed" }> = [];
    steps.forEach((s, i) => {
      if (s.onRequest) arr.push({ kind: "step", index: i });
    });
    if (allRequiredReady) arr.push({ kind: "proceed" });
    return arr;
  }, [steps, allRequiredReady]);

  const [focusPos, setFocusPos] = useState(0);
  const focusPosRef = useRef(0);
  const focusTargetsRef = useRef(focusTargets);
  const stepsRef = useRef(steps);
  const allReadyRef = useRef(allRequiredReady);
  focusPosRef.current = focusPos;
  focusTargetsRef.current = focusTargets;
  stepsRef.current = steps;
  allReadyRef.current = allRequiredReady;

  // 포커스 대상이 줄어들면 범위 보정.
  useEffect(() => {
    if (focusPos > focusTargets.length - 1) {
      setFocusPos(Math.max(0, focusTargets.length - 1));
    }
  }, [focusPos, focusTargets.length]);

  const moveFocus = useCallback((delta: number) => {
    const targets = focusTargetsRef.current;
    if (targets.length === 0) return;
    const next =
      (focusPosRef.current + delta + targets.length) % targets.length;
    setFocusPos(next);
  }, []);

  // CONFIRM(가속페달): 현재 포커스된 단계의 요청 동작을 실행하고 다음 단계로 진행.
  // (브라우저가 동작당 사용자 제스처를 요구하므로 한 번에 한 단계씩.)
  const activateFocused = useCallback(() => {
    const targets = focusTargetsRef.current;
    const target = targets[focusPosRef.current];
    if (!target) return;
    if (target.kind === "proceed") {
      if (allReadyRef.current) onComplete();
      return;
    }
    const step = stepsRef.current[target.index];
    step?.onRequest?.();
    // 한 단계 처리 후 다음 미준비 단계로 포커스 진행.
    setFocusPos((prev) => {
      const len = focusTargetsRef.current.length;
      if (len <= 1) return prev;
      return (prev + 1) % len;
    });
  }, [onComplete]);

  const handleWheelEvent = useCallback(
    (event: WheelMenuEvent) => {
      switch (event) {
        case "UP":
        case "LEFT":
          moveFocus(-1);
          break;
        case "DOWN":
        case "RIGHT":
          moveFocus(1);
          break;
        case "CONFIRM":
          activateFocused();
          break;
        case "BACK":
          onLogout();
          break;
      }
    },
    [moveFocus, activateFocused, onLogout],
  );

  useWheelMenuNav({
    enabled: true,
    onEvent: handleWheelEvent,
    onConnectionChange: setWheelConnected,
    pedalConfirm: true,
    buttonConfirm: true,
  });

  // 현재 휠 포커스가 가리키는 단계 인덱스 / 진행 버튼 여부.
  const focusedStepIndex =
    focusTargets[focusPos]?.kind === "step"
      ? (focusTargets[focusPos] as { kind: "step"; index: number }).index
      : -1;
  const proceedFocused = focusTargets[focusPos]?.kind === "proceed";

  return (
    <div className="ds-setup">
      <div className="ds-card">
        <div className="ds-head">
          <h1 className="ds-title">장치 준비</h1>
          <p className="ds-sub">
            {loginId} · 차량 선택 전 콕핏 장치/권한을 확인합니다.
          </p>
        </div>

        <div className="ds-steps">
          {steps.map((step, index) => {
            const display =
              step.key.startsWith("display-") && step.key !== "display-"
                ? statusByRole[step.key.slice("display-".length) as ExternalDisplayRole]
                : undefined;
            return (
              <div
                key={step.key}
                className={[
                  "ds-step",
                  step.readiness === "ready" ? "ready" : "",
                  step.readiness === "na" ? "na" : "",
                  index === focusedStepIndex ? "wheel-focused" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                data-testid={`ds-step-${step.key}`}
              >
                <span className="ds-step-icon">{step.icon}</span>
                <div className="ds-step-body">
                  <span className="ds-step-label">{step.label}</span>
                  {display ? (
                    <span className="ds-substatus-row">
                      <SubStatus label="열림" ok={display.open} />
                      <SubStatus label="전체화면" ok={display.fullscreen} />
                    </span>
                  ) : (
                    <span className="ds-step-hint">{step.hint}</span>
                  )}
                  {display && !display.fullscreen && display.open && (
                    <span className="ds-step-hint ds-step-hint--accent">
                      창이 모니터를 가득 채우면 준비됨 (가속페달로 재배치 · 클릭 시 주소창까지 제거)
                    </span>
                  )}
                </div>
                <StatusPill readiness={step.readiness} />
                {step.onRequest && (
                  <button
                    type="button"
                    className="ds-req-btn"
                    onClick={step.onRequest}
                    disabled={requesting === step.key}
                    data-testid={`ds-request-${step.key}`}
                  >
                    {step.requestLabel}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="ds-foot">
          <p className="ds-proceed-hint">
            {allRequiredReady
              ? "모든 항목이 준비되었습니다. 차량 선택으로 진행하세요."
              : "가속페달(또는 X/A·클릭)로 미준비 항목을 하나씩 준비하세요."}
          </p>
          <button
            type="button"
            className={`ds-proceed-btn${proceedFocused ? " wheel-focused" : ""}`}
            disabled={!allRequiredReady}
            onClick={onComplete}
            data-testid="ds-proceed-btn"
          >
            다음 (차량 선택)
          </button>
          <div className="ds-logout-row">
            <button
              type="button"
              className="ds-logout-btn"
              onClick={onLogout}
              data-testid="ds-logout-btn"
            >
              로그아웃
            </button>
          </div>
        </div>
      </div>

      <WheelHelpOverlay
        connected={wheelConnected}
        contextHint="휠 회전: 항목 이동 · 가속페달/X/A: 요청·진행 · 브레이크/○/B: 로그아웃"
      />
    </div>
  );
};

DeviceSetupComponent.displayName = "DeviceSetup";

export const DeviceSetup = React.memo(DeviceSetupComponent);
